import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

// PrimeNG Modules
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Services
import { AuthService } from '../services/auth.service';
import { UserSessionService } from '../services/mock-auth.service';
import { AuthorizationService } from '../services/authorization.service';
import { ProfessionalsService } from '../services/professionals.service';
import { AppUser, AppRole, Permission, getRoleLabel } from '../models/permissions.model';
import { environment } from '../../../environments/environment';
import { PostLoginStatusComponent } from '../shared/post-login-status/post-login-status.component';

interface RoleOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-auth-test',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    DividerModule,
    MessageModule,
    ToastModule,
    PostLoginStatusComponent,
  ],
  providers: [MessageService],
  template: `
    <div class="auth-test-container">
      <p-toast role="status" aria-live="polite" aria-atomic="true"></p-toast>
      
      <!-- Warning Message -->
      <p-message 
        severity="warn" 
        text="⚠️ DEVELOPMENT ONLY - This page is for testing authentication"
        styleClass="mb-4"
      />

      <div class="grid">
        <!-- Mock Login Card -->
        <div class="col-12 lg:col-6">
          <p-card header="🔑 Authentication">
            <p class="mb-3"><strong>Option 1:</strong> Mock Login (Quick Testing)</p>
            
            <div class="field">
              <label for="role" class="block mb-2">Role</label>
              <select 
                [ngModel]="selectedRole()" 
                (ngModelChange)="selectedRole.set($event)"
                id="role"
                class="w-full p-2 border-1 border-300 border-round"
              >
                <option value="">Select a role...</option>
                @for (role of roleOptions; track role.value) {
                  <option [value]="role.value">{{ role.label }}</option>
                }
              </select>
            </div>

            <!-- Professional ID field (shown only for professional and collaborator roles) -->
            @if (showProfessionalIdField()) {
              <div class="field bg-orange-50 p-3 border-round border-1 border-orange-200">
                <label for="professionalId" class="block mb-2 text-orange-600 font-semibold">
                  <i class="pi pi-id-card mr-1"></i>
                  Professional ID (Demo)
                </label>
                <input
                  type="text"
                  [ngModel]="professionalId()"
                  (ngModelChange)="professionalId.set($event)"
                  id="professionalId"
                  placeholder="es: FD-00001, FD-00002, ecc."
                  class="w-full p-2 border-1 border-300 border-round"
                />
                <small class="block mt-2 text-xs text-600">
                  <i class="pi pi-info-circle mr-1"></i>
                  Inserisci l'ID del fiduciario esistente nel backend
                </small>
              </div>
            }

            <p-button
              label="Mock Login"
              icon="pi pi-sign-in"
              (onClick)="mockLogin()"
              [disabled]="!selectedRole()"
              styleClass="w-full"
            />

            <p-divider>OR</p-divider>

            <p class="mb-3"><strong>Option 2:</strong> Real Azure AD Token (PowerShell)</p>
            
            <div class="field">
              <label for="manualToken" class="block mb-2">
                Access Token (JWT):
              </label>
              <textarea
                [(ngModel)]="manualToken"
                id="manualToken"
                placeholder="Paste JWT token here... (eyJhbGciOi...)"
                rows="4"
                class="w-full p-2 border-1 border-300 border-round"
                style="font-family: monospace; font-size: 0.875rem;"
              ></textarea>
            </div>

            <div class="field">
              <label for="refreshToken" class="block mb-2">
                Refresh Token <span class="text-500 font-normal">(opzionale — necessario per testare rigenerazione token)</span>:
              </label>
              <textarea
                [(ngModel)]="refreshToken"
                id="refreshToken"
                placeholder="Paste refresh token here..."
                rows="2"
                class="w-full p-2 border-1 border-300 border-round"
                style="font-family: monospace; font-size: 0.875rem;"
              ></textarea>
            </div>

            <!-- Two options for token-based auth -->
            <div class="grid">
              <div class="col-6">
                <p-button
                  label="Direct Login"
                  icon="pi pi-key"
                  (onClick)="setManualToken()"
                  [disabled]="!manualToken"
                  styleClass="w-full"
                  severity="secondary"
                />
                <small class="block mt-1 text-xs text-center text-600">
                  Skip post-login flow
                </small>
              </div>
              <div class="col-6">
                <p-button
                  label="Simulate OAuth Callback"
                  icon="pi pi-sign-in"
                  (onClick)="simulateOAuthCallback()"
                  [disabled]="!manualToken"
                  styleClass="w-full"
                  severity="warn"
                  [outlined]="true"
                />
                <small class="block mt-1 text-xs text-center text-600">
                  Full callback flow
                </small>
              </div>
            </div>

            <!-- Post-login status (shared component, same as real OAuth callback) -->
            <div class="mt-3">
              <app-post-login-status />
            </div>
            
            <small class="block mt-2 text-xs text-600">
              <i class="pi pi-info-circle mr-1"></i>
              <strong>Simulate OAuth Callback:</strong> Se il token non ha ruoli → chiama POST /api/auth/post-login → attende 20s → rigenera token → riverifica ruoli
            </small>
          </p-card>
        </div>

        <!-- Current User Card -->
        <div class="col-12 lg:col-6">
          <p-card header="👤 Current User">
            @if (currentUser(); as user) {
              <div class="user-info">
                <p><strong>ID:</strong> {{ user.id }}</p>
                <p><strong>Name:</strong> {{ user.name }}</p>
                <p><strong>Email:</strong> {{ user.email }}</p>
                <p><strong>Roles:</strong> {{ user.roles.join(', ') }}</p>
                @if (user.professionalId) {
                  <p><strong>Professional ID:</strong> {{ user.professionalId }}</p>
                }
                <p><strong>Token Expires:</strong> {{ formatExpiration(user.exp) }}</p>
                
                <p-divider />
                
                <p-button
                  label="Logout"
                  icon="pi pi-sign-out"
                  (onClick)="logout()"
                  severity="danger"
                  styleClass="w-full"
                />
              </div>
            } @else {
              <p-message 
                severity="info" 
                text="No user logged in"
              />
            }
          </p-card>
        </div>

        <!-- API Test Card -->
        <div class="col-12">
          <p-card header="🔌 API Test - Suppliers Registry">
            <p class="mb-3">
              Test if the authentication token is being sent to the API:
            </p>

            <div class="flex gap-2 mb-3">
              <p-button
                label="GET /api/v1/suppliers"
                icon="pi pi-arrow-right"
                (onClick)="testSuppliersApi()"
                [loading]="isTestingApi()"
              />
              
              <p-button
                label="Clear Results"
                icon="pi pi-times"
                (onClick)="clearApiResults()"
                severity="secondary"
                [disabled]="!apiTestResult()"
              />
            </div>

            @if (apiTestResult(); as result) {
              <div class="api-result" [class.success]="result.success" [class.error]="!result.success">
                <p><strong>Status:</strong> {{ result.status }}</p>
                <p><strong>Message:</strong> {{ result.message }}</p>
                
                @if (result.tokenSent) {
                  <p class="success-text">✅ Token was sent in Authorization header</p>
                } @else {
                  <p class="error-text">❌ Token was NOT sent</p>
                }

                @if (result.data) {
                  <p-divider />
                  <p><strong>Response Preview:</strong></p>
                  <pre class="response-preview">{{ formatJson(result.data) }}</pre>
                }
              </div>
            }
          </p-card>
        </div>

        <!-- Environment Info -->
        <div class="col-12">
          <p-card header="⚙️ Environment Configuration">
            <div class="env-info">
              <p><strong>Environment:</strong> {{ environment.production ? 'Production' : 'Development' }}</p>
              <p><strong>Use Mock Auth:</strong> {{ environment.useMockAuth ? 'Yes' : 'No' }}</p>
              <p><strong>Backoffice API:</strong> {{ environment.backofficeApiUrl }}</p>
              @if (environment.azureAd) {
                <p><strong>Azure AD Tenant:</strong> {{ environment.azureAd.tenantId }}</p>
                <p><strong>Azure AD Client:</strong> {{ environment.azureAd.clientId }}</p>
              }
              @if (hasStaticToken()) {
                <p class="warning-text">⚠️ <strong>Static Test Token:</strong> Configured (will be used automatically)</p>
              }
            </div>
          </p-card>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-test-container {
      padding: 2rem;
      max-width: 1400px;
      margin: 0 auto;
    }

    .user-info p {
      margin: 0.5rem 0;
    }

    .env-info p {
      margin: 0.5rem 0;
    }

    .api-result {
      padding: 1rem;
      border-radius: 6px;
      background: #f8f9fa;
      border-left: 4px solid #6c757d;
    }

    .api-result.success {
      background: #d4edda;
      border-left-color: #28a745;
    }

    .api-result.error {
      background: #f8d7da;
      border-left-color: #dc3545;
    }

    .api-result p {
      margin: 0.5rem 0;
    }

    .success-text {
      color: #28a745;
      font-weight: 600;
    }

    .error-text {
      color: #dc3545;
      font-weight: 600;
    }

    .warning-text {
      color: #ff9800;
      font-weight: 600;
    }

    .response-preview {
      background: #2d2d2d;
      color: #f8f8f2;
      padding: 1rem;
      border-radius: 4px;
      overflow-x: auto;
      max-height: 300px;
      font-size: 0.875rem;
    }

    textarea {
      font-family: 'Courier New', monospace;
      font-size: 0.875rem;
    }
  `],
})
export class AuthTestComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private mockAuthService = inject(UserSessionService);
  private authzService = inject(AuthorizationService);
  private professionalsService = inject(ProfessionalsService);
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  private router = inject(Router);

  // Expose environment for template
  environment = environment;

  // Signals - NOW using AppUser from AuthorizationService
  currentUser = computed(() => this.authzService.currentUser());
  isTestingApi = signal(false);
  apiTestResult = signal<any>(null);
  selectedRole = signal<string>('');
  professionalId = signal<string>('');

  // Form state
  manualToken = '';
  refreshToken = '';

  // Computed: show professional ID field only for professional and collaborator roles
  showProfessionalIdField = computed(() => {
    return this.selectedRole() === 'professional' || this.selectedRole() === 'collaborator';
  });

  // Role options
  roleOptions: RoleOption[] = [
    { label: ' Admin (Super User)', value: 'admin' },
    { label: '👔 Backoffice Admin', value: 'backoffice_admin' },
    { label: '👥 Backoffice Professionals', value: 'backoffice_professional' },
    { label: '🏢 Backoffice Supplier', value: 'backoffice_supplier' },
    { label: '⚖️ Professional', value: 'professional' },
    { label: '🤝 Collaborator', value: 'collaborator' },
  ];

  ngOnInit(): void {
    // 🚫 Block access to auth-test in production
    if (environment.production) {
      console.warn('⚠️ Auth-test is not available in production');
      this.router.navigate(['/login']);
      return;
    }

    // No need to subscribe - using computed signal from AuthorizationService
    console.log('🎭 Auth Test Page loaded');
    console.log('👤 Current user:', this.currentUser());
  }

  ngOnDestroy(): void {}

  mockLogin(): void {
    const role = this.selectedRole();
    const profId = this.professionalId();
    
    if (!role) return;

    console.log(`🎭 Mock login as: ${role}`, profId ? `with Professional ID: ${profId}` : '');
    
    // Set demo professional ID if provided (for professional or collaborator roles)
    if (profId && profId.trim()) {
      this.mockAuthService.setDemoProfessionalId(profId.trim());
      console.log(`✅ Demo Professional ID set: ${profId.trim()}`);
    } else {
      this.mockAuthService.clearDemoProfessionalId();
    }
    
    this.mockAuthService.loginAs(role as any);
    
    const detail = profId 
      ? `Logged in as ${role} (Professional ID: ${profId})`
      : `Logged in as ${role}`;
    
    this.messageService.add({
      severity: 'success',
      summary: 'Mock Login',
      detail: detail,
    });

    // No need to refreshUserInfo - AuthorizationService updates automatically
    
    // Redirect based on role - using official redirect logic
    setTimeout(() => {
      const user = this.authService.getCurrentUser();
      const isProfessionalOrCollaborator = role === 'professional' || role === 'collaborator';
      const isBackofficeUser = 
        role === 'backoffice_supplier' || 
        role === 'backoffice_professional' ||
        role === 'backoffice_admin' ||
        role === 'admin';
      
      if (isProfessionalOrCollaborator) {
        // Check professional profile via simulated API
        this.mockAuthService.simulateGetAuthProfile().subscribe({
          next: (profile) => {
            console.log(`✅ Professional profile found for ${role}, redirecting to registration progress`);
            sessionStorage.setItem('professional_profile', JSON.stringify(profile));
            this.router.navigate(['/axa/professionals-registration-progress']);
          },
          error: (error) => {
            console.log(`⚠️ No professional profile for ${role}, redirecting to pre-registration`);
            this.router.navigate(['/axa/pre-registration']);
          }
        });
      } else if (isBackofficeUser) {
        const targetDashboard = role === 'backoffice_supplier'
          ? '/axa/suppliers-dashboard'
          : '/axa/professionals-dashboard';
        console.log(`💼 Utente backoffice (${role}), redirect a ${targetDashboard}`);
        this.router.navigate([targetDashboard]);
      } else {
        console.log(`✅ Redirecting ${role} to login page`);
        this.router.navigate(['/login']);
      }
    }, 500);
  }

  /**
   * Simula il flusso OAuth callback completo con il token incollato.
   * Usa lo stesso metodo della callback reale: completeLoginWithToken().
   * Lo stato viene mostrato dal componente PostLoginStatusComponent (stesso della callback).
   */
  async simulateOAuthCallback(): Promise<void> {
    if (!this.manualToken.trim()) return;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎭 SIMULAZIONE OAUTH CALLBACK');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      const token = this.manualToken.trim();
      this.manualToken = '';

      // NOTE: The refresh token field in the UI is kept for UX compatibility
      // but has no effect — the BFF now manages the refresh_token via HttpOnly cookie.
      // In local dev, refreshAzureToken() will simply return null (no cookie),
      // and the post-login flow continues with the pasted access_token.
      this.refreshToken = '';

      // Stessa logica della callback OAuth reale
      const success = await this.authService.completeLoginWithToken(token);
      this.authzService.refreshUser();

      if (!success) {
        const denial = this.authService.accessDenialReason$.getValue();
        this.messageService.add({
          severity: 'error',
          summary: denial ? 'Accesso negato' : 'Post-login fallito',
          detail: denial ?? 'Nessun ruolo assegnato dopo il post-login.',
          life: 8000,
        });
      }

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    } catch (error) {
      console.error('❌ Errore durante la simulazione OAuth callback:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore simulazione',
        detail: 'Token non valido o errore nel flusso.',
      });
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  }

  async setManualToken(): Promise<void> {
    if (!this.manualToken.trim()) return;

    try {
      const token = this.manualToken.trim();
      
      // Use AuthService to properly parse and store the token
      // This ensures the token is treated exactly like a real Azure AD login
      this.authService.setToken(token);

      // Load effective roles from the server (/me/roles) so Direct Login behaves
      // like the real callback: onboarding users whose roles are not in the JWT
      // are still recognized and guards don't block navigation locally.
      // Fails silently, falling back to JWT-decoded roles.
      await this.authService.loadServerRoles();

      // Persist token so that _initializeSession() can restore it on F5
      // (same mechanism used by MockAuthService.loginAs)
      sessionStorage.setItem('mock_access_token', token);
      
      // ✅ Refresh AuthorizationService to update UI (headerbar will see new user data)
      this.authzService.refreshUser();

      this.messageService.add({
        severity: 'success',
        summary: 'Authenticated',
        detail: 'Token configured successfully',
      });

      this.manualToken = '';
      
      // ✅ Use the centralized redirect logic from AuthService
      // This ensures consistent behavior with OAuth callback
      await this.authService.redirectAfterAuthentication();
      
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Token',
        detail: 'The token format is invalid or expired',
      });
    }
  }

  logout(): void {
    // Clear authentication data without redirecting
    this.mockAuthService.clearAuth();
    
    // Reset form fields
    this.selectedRole.set('');
    this.professionalId.set('');
    
    this.messageService.add({
      severity: 'info',
      summary: 'Logged Out',
      detail: 'Utente rimosso - puoi mockare un profilo diverso',
    });
  }

  async testSuppliersApi(): Promise<void> {
    this.isTestingApi.set(true);
    this.apiTestResult.set(null);

    const token = this.authService.getAccessToken();
    const url = `${environment.backofficeApiUrl}/api/v1/suppliers`;

    console.log('🔌 Testing API:', url);
    console.log('🔑 Token present:', !!token);

    try {
      // The interceptor will automatically add the token
      const response = await this.http.get(url).toPromise();

      this.apiTestResult.set({
        success: true,
        status: 200,
        message: 'API call successful',
        tokenSent: !!token,
        data: response,
      });

      this.messageService.add({
        severity: 'success',
        summary: 'API Test',
        detail: '✅ Request successful - Token was sent!',
      });
    } catch (error: any) {
      console.error('❌ API call failed:', error);

      this.apiTestResult.set({
        success: false,
        status: error.status || 0,
        message: error.message || 'Unknown error',
        tokenSent: !!token,
        data: error.error,
      });

      this.messageService.add({
        severity: 'error',
        summary: 'API Test Failed',
        detail: `Status ${error.status}: ${error.message}`,
      });
    } finally {
      this.isTestingApi.set(false);
    }
  }

  clearApiResults(): void {
    this.apiTestResult.set(null);
  }

  // Helper methods for template
  getRoleLabel(role: AppRole): string {
    return getRoleLabel(role);
  }

  formatExpiration(exp?: number): string {
    if (!exp) return 'N/A';
    const date = new Date(exp * 1000);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    
    if (diff < 0) {
      return 'EXPIRED';
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return `${date.toLocaleString()} (in ${days} days)`;
  }

  formatJson(obj: any): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  hasStaticToken(): boolean {
    return !!(environment as any).staticTestToken;
  }
}
